import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Grid, Stack, Box, Button} from 'views/components';
import {Translate} from 'views/module';

import Filter from './filter/Filter';
import List from './list/List';
import MapFilter from './filter/MapFilter';

import {simplifyQuestions} from 'utils/user_bio';
import {generateFilterOptions} from './filter/FilterGenerator';
import backend from 'utils/backend';

const filterOptions = generateFilterOptions();

const defaultSelection = () => {
  return {
    order: {orderBy: 'creationTime', orderDirc: 'DESC'},
    filter: {
      status: {
        filterData: filterOptions['status'],
        optionSelected: [
          'ACTIVE',
          'PROFILE_REVIEW',
        ],
      },
      location: {
        filterData: filterOptions['location'],
        optionSelected: 'enabled',
      },
    },
  };
};

const CaregiverList = (props) => {
  const {jobLocation, selectedCaregivers, setSelectedCaregivers} = props;
  const [selection, setSelection] = React.useState(defaultSelection());
  const [data, setData] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [mapBounds, setMapBounds] = React.useState(null);
  const [highlightedCaregiverId, setHighlightedCaregiverId] = React.useState(null);
  const [caregiversForMap, setCaregiversForMap] = React.useState([]);
  // Used to cancel stale loadData results: increment to mark previous requests stale
  const requestIdRef = React.useRef(0);

  const resetFilter = () => {
    setSelection(defaultSelection());
  };

  const updateFilter = (filter, option) => {
    setSelection((prev) => {
      const newFilter = {...prev.filter};

      if (option) {
        if (filter.multiSelect) {
          const selected = newFilter[filter.key]?.optionSelected || [];

          const doesItExist = selected.find((k) => k === option.key);
          if (!doesItExist) {
          // Add option
            newFilter[filter.key] = {
              filterData: filter,
              optionSelected: [...selected, option.key],
            };
          } else {
            // Remove option
            newFilter[filter.key] = {
              ...newFilter[filter.key],
              optionSelected: selected.filter((k) => k !== option.key),
            };
          }
        } else {
          // Single select: replace value
          newFilter[filter.key] = {
            filterData: filter,
            optionSelected: option.key,
          };
        }
      } else {
        // Remove filter entirely
        delete newFilter[filter.key];
      }

      setLastEvaluatedKey(null);
      return {...prev, filter: newFilter};
    });
  };

  // Check if map filter is enabled
  const isMapFilterEnabled = selection.filter.location?.optionSelected === 'enabled';

  const loadData = React.useCallback(async () => {
    // Capture current request id so this call can be ignored if a newer one starts
    const reqId = requestIdRef.current;
    setIsLoading(true);
    try {
      const filter = [];
      for (const filterSelected of Object.values(selection.filter)) {
        if (filterSelected.filterData.key === 'location') continue;

        if (filterSelected.filterData.key === 'certification') {
          filter.push(...filterSelected.optionSelected.map((optKey) => ({key: optKey, value: true})));
          continue;
        }

        if (filterSelected.filterData.key === 'languages') {
          const labels = filterSelected.optionSelected.map((k) => filterOptions.languages.options[k].label);
          filter.push({key: 'languages', value: labels.join(' ')});
          continue;
        }

        filter.push({key: filterSelected.filterData.key, value: filterSelected.optionSelected});
      }

      // Add buffer bounding box filters when map filter is enabled and buffer bounds are set
      if (isMapFilterEnabled && mapBounds) {
        filter.push({key: 'latitude', value: `[${mapBounds.minLat} TO ${mapBounds.maxLat}]`});
        filter.push({key: 'longitude', value: `[${mapBounds.minLng} TO ${mapBounds.maxLng}]`});
      }

      const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
      const result = await backend.caregiver.queryCaregiver(filter, sortBy, lastEvaluatedKey);

      // If another request started after this one, drop the results (cancelled/stale)
      if (reqId !== requestIdRef.current) return;

      setData((e) => [...e, ...result.list]);
      setLastEvaluatedKey(result.lastEvaluatedKey);
    } finally {
      // Only update loading state when this call is still the latest
      if (reqId === requestIdRef.current) setIsLoading(false);
    }
  }, [selection, lastEvaluatedKey, mapBounds]);

  React.useEffect(() => {
    // Bump request id to mark any in-flight requests stale/cancelled
    requestIdRef.current += 1;

    setData([]);
    setLastEvaluatedKey(null);
    setHighlightedCaregiverId(null);
    loadData();
  }, [selection, mapBounds]); // Reset when filters, reload, or bounds change

  const handleMapCaregiverClick = React.useCallback((caregiverId) => {
    setHighlightedCaregiverId(caregiverId);
  }, []);

  const handleSelectAll = React.useCallback(() => {
    const allCaregiverIds = data.map((caregiver) => caregiver.caregiverRn);
    setSelectedCaregivers((prev) => {
      const newSelected = new Set([...(prev), ...allCaregiverIds]);
      return Array.from(newSelected);
    });
  }, [data, setSelectedCaregivers]);

  const handleDeselectAll = React.useCallback(() => {
    setSelectedCaregivers([]);
  }, [setSelectedCaregivers]);

  React.useEffect(() => {
    const list = data.map((caregiver) => {
      const bio = simplifyQuestions(caregiver.bio?.questions ?? {});
      const firstName = (bio?.firstName || '').trim();
      const lastName = (bio?.lastName || '').trim();
      const profileImg = bio?.profilePhoto?.[0]?.src;

      const name = [firstName, lastName].filter(Boolean).join(' ') || caregiver.caregiverName || 'Caregiver';

      return {
        id: caregiver.caregiverRn,
        name,
        profileImg,
        location: {
          lat: caregiver.latitude || null,
          lng: caregiver.longitude || null,
        },
      };
    }).filter((c) => c.location.lat && c.location.lng);

    setCaregiversForMap(list);
  }, [data]);


  const onBoundChange = (e) => {
    setMapBounds(e);
    setLastEvaluatedKey(null);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Stack spacing={2} direction={'row'}>
          <Filter
            selection={selection}
            filterOptions={filterOptions}
            updateFilter={updateFilter}
            onClearFilter={resetFilter}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              onClick={handleSelectAll}
              disabled={data.length === 0}
              variant="outlined"
              size="small"
            >
              <Translate expression="career.create.Review.SelectAll" />
            </Button>
            <Button
              onClick={handleDeselectAll}
              disabled={selectedCaregivers.length === 0}
              variant="outlined"
              size="small"
            >
              <Translate expression="career.create.Review.DeselectAll" />
            </Button>
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={isMapFilterEnabled ? 6 : 12}>
            {
              isMapFilterEnabled &&
              <Box sx={{height: '600px', overflowY: 'auto', pr: 1}}>
                <List
                  data={data}
                  isLoading={isLoading}
                  loadMore={loadData}
                  hasMore={!!lastEvaluatedKey}
                  highlightedCaregiverId={highlightedCaregiverId}
                  selectedCaregivers={selectedCaregivers}
                  setSelectedCaregivers={setSelectedCaregivers}
                />
              </Box>
            }
            {
              !isMapFilterEnabled &&
              <Box sx={{maxHeight: '600px', overflowY: 'auto'}}>
                <List
                  data={data}
                  isLoading={isLoading}
                  loadMore={loadData}
                  hasMore={!!lastEvaluatedKey}
                  highlightedCaregiverId={highlightedCaregiverId}
                  selectedCaregivers={selectedCaregivers}
                  setSelectedCaregivers={setSelectedCaregivers}
                />
              </Box>
            }
          </Grid>
          {
            isMapFilterEnabled &&
            <Grid item xs={12} md={6}>
              <MapFilter
                caregivers={caregiversForMap}
                onBoundsChange={onBoundChange}
                onMarkerClick={handleMapCaregiverClick}
                jobLocation={jobLocation}
                selectedCaregivers={selectedCaregivers}
                onCaregiverSelect={handleMapCaregiverClick}
              />
            </Grid>
          }
        </Grid>
      </Grid>
    </Grid>
  );
};

CaregiverList.propTypes = {
  jobLocation: PropTypes.object,
  selectedCaregivers: PropTypes.array.isRequired,
  setSelectedCaregivers: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaregiverList));
